import { useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  Typography,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Card,
  CardContent,
  SvgIcon,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import AddIcons from "./GamepageImage/plus_icon.svg";
import SubIcon from "./GamepageImage/Minus_icon.svg";
import MulIcon from "./GamepageImage/Multip_icon.svg";
import DivIcon from "./GamepageImage/Subtr_icon.svg";
import "./skillstrainer.scss";
import { ReactComponent as clocklist } from "./GamepageImage/TimeSetIcon.svg";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import {
  addArithmeticGameSkill,
  clearAddArithmeticGameSkillResponse,
} from "../../Redux/Slices/ArithmeticGameSkill/AddArithmeticGameSkillSlice";
import { useNavigate } from "react-router-dom";
import Menus from "../../Components/Menu/Menu";
import { clearListuser, listUser } from "../../Redux/Slices/User/ListUserSlice";
import { clearLoginStatus } from "../../Redux/Slices/LoginSlicer/LoginSlicer";
import IconButtonField from "../../Components/Button/IconButtonField";
import Avatars from "../../Components/Avatar/Avatars";
import { clearDashboardChart } from "../../Redux/Slices/DashboardChart/DashboardChart";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import { clearAddMemoryGameSkillResponse } from "../../Redux/Slices/MemoryGameSkill/AddMemoryGameSkillSlice";
import UserProfileSection from "../../Components/UserProfileSection/UserProfileSection";

const schema = z.object({
  operation: z
    .array(z.enum(["add", "subtract", "multiply", "divide"]))
    .min(1, { message: "Select at least one operation" }),

  minValue: z.coerce
    .number({ invalid_type_error: "Enter a number" })
    .min(1, { message: "Minimum value must be at least 1" }),

  maxValue: z.coerce
    .number({ invalid_type_error: "Enter a number" })
    .min(1, { message: "Maximum value must be at least 1" }),

  timeLimit: z.enum(["30", "60", "120", "240"]).optional(),
});
// .refine((data) => data.minValue <= data.maxValue, {
//   message: "Minimum value must be less than maximum value",
//   path: ["minValue"], // Error will show under maxValue field
// });

type FormData = z.infer<typeof schema>;

const GameFlow = () => {
  const dispatch = useAppDispatch();
  const nav = useNavigate();

  const {
    control,
    handleSubmit,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      operation: ["add"],
      minValue: undefined,
      maxValue: undefined,
      timeLimit: "30",
    },
  });

  const selectedOps = watch("operation");

  const handleOpChange = (value: FormData["operation"][number]) => {
    const updated = selectedOps.includes(value)
      ? selectedOps.length > 1
        ? selectedOps.filter((op) => op !== value)
        : selectedOps
      : [...selectedOps, value];

    setValue("operation", updated, { shouldValidate: true });
  };

  const handleStartArithmeticGame = () => {
    handleSubmit(
      () => {
        const formData = getValues();
        if (formData.operation.length === 0) return;

        const payload: any = {
          operation: formData.operation,
        };

        if (formData.minValue !== undefined) {
          payload.min_num = Number(formData.minValue);
        }

        if (formData.maxValue !== undefined) {
          payload.max_num = Number(formData.maxValue);
        }

        const time = Number(formData.timeLimit);
        sessionStorage.setItem("timergame", time.toString());
        sessionStorage.setItem(
          "arithmeticGameSettings",
          JSON.stringify(payload)
        );

        dispatch(clearAddArithmeticGameSkillResponse());
        dispatch(addArithmeticGameSkill(payload));
        nav("/arithmetic-game");
      },
      // On error, do nothing (errors will show below fields)
      () => { }
    )();
  };

  const timeOptions = ["30", "60", "120", "240"];

  const icons = [
    {
      src: "Number Memory",
      round: "#FBBF24",
      color: "#faf5ff",
      text: "You will see a number on the screen for a few seconds.",
      secondtext: "Memorize the number and enter it exactly as you saw it.",
    },
    {
      src: "Letter Memory",
      round: "#60A5FA",
      color: "#faf5ff",
      text: "You will see a set of letters on the screen.",
      secondtext:
        "Memorize the sequence and enter the letters in the correct order.",
    },
    {
      src: "Word Order Memory",
      round: "#F87171",
      color: "#f0fdf4",
      text: "You will see a list of words displayed one by one.",
      secondtext:
        "Memorize the words and enter them in the exact order they were shown",
    },
  ];

  useEffect(() => {
    dispatch(listUser());
  }, [dispatch]);

  const { data: ListUserSliceLoading } = useAppSelector(
    (state) => state.ListUserSlice
  );
  const ListUser = Array.isArray(ListUserSliceLoading?.data)
    ? ListUserSliceLoading?.data.map((user, index) => ({
      ...user,
      index: index + 1,
    }))
    : [];

  const operationOptions: {
    value: FormData["operation"][number];
    icon: string;
    label: string;
  }[] = [
      { value: "add", icon: AddIcons, label: "Addition" },
      { value: "subtract", icon: SubIcon, label: "Subtraction" },
      { value: "multiply", icon: MulIcon, label: "Multiplication" },
      { value: "divide", icon: DivIcon, label: "Division" },
    ];

  return (
    <div className="SkillStudentAlingment">
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "20px",
        }}
      >
        <div>
          <div
            className="DashboardStudent"
            style={{ fontWeight: "700", fontSize: "24px" }}
          >
            Skill Trainers
          </div>
          <div className="StudentPractice">
            Enhance your preparation with interactive exercises
          </div>
        </div>
        <UserProfileSection />
      </div>


      {/* Games Section */}
      <Box>
        <Grid container spacing={2}>
          {/* Arithmetic Game */}
          <Grid item xs={12} sm={12} md={12} lg={6}>
            <Card>
              <CardContent>
                <Typography className="titlegame" gutterBottom>
                  Arithmetic Game
                </Typography>
                <Typography variant="body2" className="subtitle" gutterBottom>
                  Practice your maths with flexible training settings
                </Typography>
                <form>
                  <Grid container spacing={1} mb={2}>
                    {operationOptions.map(({ value, icon, label }) => (
                      <Grid item xs={3} key={value}>
                        <Button
                          fullWidth
                          variant={
                            selectedOps.includes(value) ? "outlined" : "text"
                          }
                          onClick={() => handleOpChange(value)}
                          type="button"
                        >
                          <img src={icon} alt={label} />
                        </Button>
                        <div
                          className="labelnamegame"
                          style={{ textAlign: "center" }}
                        >
                          {label}
                        </div>
                      </Grid>
                    ))}
                    {errors.operation && (
                      <Grid item xs={12}>
                        <Typography
                          color="error"
                          sx={{ marginLeft: "15px" }}
                          variant="caption"
                        >
                          {errors.operation.message}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>

                  <Grid container spacing={2} mb={2} sx={{ marginTop: "10px" }}>
                    {/* Min Value */}
                    <Grid item xs={6}>
                      <div className="labelnamegames">Min Value</div>
                      <Controller
                        name="minValue"
                        control={control}
                        rules={{
                          required: "Min value is required",
                          validate: (value) => {
                            const max = watch("maxValue");
                            return (
                              value <= max ||
                              "Min value must be less than Max value"
                            );
                          },
                        }}
                        render={({ field }) => (
                          <>
                            <TextField
                              {...field}
                              type="number"
                              fullWidth
                              variant="outlined"
                              value={field.value ?? ""}
                              placeholder="Enter minimum value"
                              onChange={(e) => {
                                const val = e.target.value;
                                field.onChange(val === "" ? "" : Number(val));
                              }}
                              onBlur={() => {
                                if (!field.value || field.value < 1) {
                                  field.onChange(1);
                                }
                              }}
                              inputProps={{
                                min: 1,
                                step: 1,
                                onKeyDown: (e) => {
                                  if (["e", "E", "+", "-"].includes(e.key)) {
                                    e.preventDefault();
                                  }
                                },
                              }}
                            />
                            {errors.minValue && (
                              <Typography
                                color="error"
                                sx={{ marginLeft: "5px", fontSize: "12px" }}
                              >
                                {errors.minValue.message}
                              </Typography>
                            )}

                            {watch("maxValue") < watch("minValue") && (
                              <Typography
                                color="error"
                                sx={{ marginLeft: "5px", fontSize: "12px" }}
                              >
                                Minimum value must be less than maximum value
                              </Typography>
                            )}
                          </>
                        )}
                      />
                    </Grid>

                    {/* Max Value */}
                    <Grid item xs={6}>
                      <div className="labelnamegames">Max Value</div>
                      <Controller
                        name="maxValue"
                        control={control}
                        rules={{
                          required: "Max value is required",
                          validate: (value) => {
                            const min = watch("minValue");
                            return (
                              value < min ||
                              "Max value must be greater than Min value"
                            );
                          },
                        }}
                        render={({ field }) => (
                          <>
                            <TextField
                              {...field}
                              type="number"
                              fullWidth
                              variant="outlined"
                              placeholder="Enter maximum value"
                              value={field.value ?? ""}
                              onChange={(e) => {
                                const val = e.target.value;
                                field.onChange(val === "" ? "" : Number(val));
                              }}
                              onBlur={() => {
                                if (!field.value || field.value < 1) {
                                  field.onChange(1);
                                }
                              }}
                              inputProps={{
                                min: 1,
                                step: 1,
                                onKeyDown: (e) => {
                                  if (["e", "E", "+", "-"].includes(e.key)) {
                                    e.preventDefault();
                                  }
                                },
                              }}
                            />
                            {errors.maxValue && (
                              <Typography
                                color="error"
                                sx={{ marginLeft: "5px", fontSize: "12px" }}
                              >
                                {errors.maxValue.message}
                              </Typography>
                            )}
                          </>
                        )}
                      />
                    </Grid>
                  </Grid>

                  <div className="labelnamegames">Time Limit</div>
                  <Box mt={2}>
                    <Grid
                      container
                      spacing={2}
                      display={"flex"}
                      flexDirection={"row"}
                    >
                      {timeOptions.map((time) => (
                        <Grid item key={time}>
                          <Controller
                            name="timeLimit"
                            control={control}
                            render={({ field }) => (
                              <ToggleButtonGroup
                                value={field.value}
                                exclusive
                                onChange={(_, val) => field.onChange(val)}
                              >
                                <ToggleButton value={time}>
                                  <SvgIcon component={clocklist} />
                                  <span style={{ fontSize: "14px" }}>
                                    {time}s
                                  </span>
                                </ToggleButton>
                              </ToggleButtonGroup>
                            )}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>

                  <Box mt={2}>
                    <button
                      className={
                        errors?.minValue?.message ||
                          errors?.maxValue?.message ||
                          watch("maxValue") < watch("minValue")
                          ? "disabled_Examprogesspagecontinue"
                          : "Examprogesspagecontinue"
                      }
                      type="button"
                      onClick={handleStartArithmeticGame}
                      disabled={
                        errors?.minValue?.message ||
                          errors?.maxValue?.message ||
                          watch("maxValue") < watch("minValue")
                          ? true
                          : false
                      }
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px",
                        padding: "10px 16px",
                        fontWeight: "500",
                        cursor: "pointer",
                        fontSize: "14px",
                        lineHeight: "20px",
                      }}
                    >
                      <PlayArrowOutlinedIcon
                        sx={{ padding: "0", fontSize: "20px" }}
                      />
                      Start Arithmetic Training
                    </button>
                  </Box>
                </form>
              </CardContent>
            </Card>
          </Grid>

          {/* Memory Game */}
          <Grid item xs={12} sm={12} md={12} lg={6}>
            <Card>
              <CardContent>
                <Typography className="titlegame" gutterBottom>
                  Memory Game
                </Typography>
                <Typography className="subtitlememory">
                  Challenge your memory across 3 fun rounds
                </Typography>
                {icons.map(({ src, round, color, text, secondtext }, index) => (
                  <Box key={index}>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "30px",
                        height: "30px",
                        borderRadius: "50%",
                        backgroundColor: round,
                        color: "white",
                        fontWeight: "bold",
                        fontSize: "16px",
                        marginRight: "8px",
                      }}
                    >
                      {index + 1}
                    </span>
                    <Box
                      bgcolor={color}
                      p={2}
                      borderRadius={2}
                      mt={"-15px"}
                      mb={"5px"}
                    >
                      <Typography sx={{ fontSize: "15px", fontWeight: 500 }}>
                        Round {index + 1}: {src}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ fontSize: "13px", color: "#4b5563" }}
                      >
                        {text}
                        <div>
                          <span style={{ fontWeight: 500, color: "#1F2937" }}>
                            Your Task: {secondtext}
                          </span>
                        </div>
                      </Typography>
                    </Box>
                  </Box>
                ))}
                <Box mt={2}>
                  <button
                    className="Examprogesspagecontinue"
                    onClick={() => {
                      nav("/memorygame");
                      dispatch(clearAddMemoryGameSkillResponse());
                    }}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      fontSize: "14px",
                      lineHeight: "20px",
                      fontWeight: "500",
                    }}
                  >
                    <PlayArrowOutlinedIcon
                      sx={{ padding: "0", fontSize: "20px" }}
                    />
                    Start Memory Challenge
                  </button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </div >
  );
};

export default GameFlow;
